import React, { useState } from "react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Search, LocateFixed, Loader2 } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";

export default function CPSearch({ size = "md", className = "" }) {
  const [cp, setCp] = useState("");
  const [locating, setLocating] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isLg = size === "lg";

  const handleLocate = () => {
    if (!navigator.geolocation) {
      toast.error("Tu navegador no soporta geolocalización");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${coords.latitude}&lon=${coords.longitude}&format=json&addressdetails=1`,
            { headers: { "Accept-Language": "es" } }
          );
          const data = await res.json();
          const postal = data?.address?.postcode?.replace(/\D/g, "").slice(0, 5);
          if (postal && postal.length === 5) {
            navigate(`/directorio/${postal}`);
          } else {
            toast.error("No pudimos obtener tu código postal. Ingrésalo manualmente.");
          }
        } catch {
          toast.error("Error al obtener tu ubicación. Ingrésalo manualmente.");
        } finally {
          setLocating(false);
        }
      },
      () => {
        toast.error("Permiso de ubicación denegado. Ingresa tu CP manualmente.");
        setLocating(false);
      },
      { timeout: 8000 }
    );
  };

  const goToCP = (value) => {
    const clean = (value || "").replace(/\D/g, "");
    if (clean.length < 5) {
      toast.error("El código postal debe tener 5 dígitos (ej. 06700)");
      return;
    }
    const target = `/directorio/${clean}`;
    // Avoid re-navigating to the same URL (some browsers behave oddly)
    if (location.pathname === target) return;
    navigate(target);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    goToCP(cp);
  };

  const handleKeyDown = (e) => {
    // Block Enter when not enough digits to prevent accidental navigations
    if (e.key === "Enter") {
      e.preventDefault();
      goToCP(cp);
    }
  };

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      <form
        onSubmit={handleSubmit}
        className="flex items-center gap-2 bg-white border border-border rounded-full p-1.5 shadow-sm"
        data-testid="cp-search-form"
        noValidate
        autoComplete="off"
      >
        <div className="pl-4 pr-2 text-muted-foreground">
          <Search className={isLg ? "w-5 h-5" : "w-4 h-4"} />
        </div>
        <Input
          value={cp}
          onChange={(e) => setCp(e.target.value.replace(/[^0-9]/g, "").slice(0, 5))}
          onKeyDown={handleKeyDown}
          placeholder="Tu código postal (ej. 06700)"
          className={`border-0 shadow-none focus-visible:ring-0 ${isLg ? "h-12 text-base" : ""}`}
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="postal-code"
          name="postal_code"
          maxLength={5}
          data-testid="cp-search-input"
        />
        <Button
          type="submit"
          disabled={cp.length < 5}
          className={`btn-primary rounded-full ${isLg ? "h-12 px-7" : "h-9 px-5"} disabled:opacity-50 disabled:cursor-not-allowed`}
          data-testid="cp-search-submit"
        >
          Ver directorio
        </Button>
      </form>
      <button
        type="button"
        onClick={handleLocate}
        disabled={locating}
        className={`btn-primary rounded-full flex items-center justify-center gap-2 ${isLg ? "h-12 px-7 text-base" : "h-9 px-5 text-sm"} disabled:opacity-50 disabled:cursor-not-allowed`}
        data-testid="cp-locate-btn"
      >
        {locating
          ? <><Loader2 className="w-4 h-4 animate-spin" /> Detectando ubicación…</>
          : <><LocateFixed className="w-4 h-4" /> Usar mi ubicación</>
        }
      </button>
    </div>
  );
}
